/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */
import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Star from '@material-ui/icons/Star';
import {
  SessionState,
  configApiRef,
  errorApiRef,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { DefaultProviderSettings, ProviderSettingsItem } from '@backstage/plugin-user-settings';
import { m42EnterpriseAuthApiRef, m42EnterpriseProviderConfig } from './m42EnterpriseAuth';

// Settings > Authentication Providers only lists Backstage's built-in provider ids, and
// m42-enterprise has no frontend-visible config schema, so it is added here on the same
// condition App.tsx uses for the sign-in page (any non-development environment). There
// Microsoft is the only sign-in (PLAT-3071):
// - Google is turned off: its row is disabled and never touches its auth API.
// - GitHub cannot be signed into from here, but the catalog's GitHub tabs (Actions,
//   pull requests, Dependabot, languages) still start a GitHub session when used, so a
//   live session keeps its Sign out.

const ProviderRow = (props: {
  title: string;
  description: string;
  action: React.ReactNode;
}) => (
  <ListItem>
    <ListItemIcon>
      <Star />
    </ListItemIcon>
    <ListItemText primary={props.title} secondary={props.description} />
    <ListItemSecondaryAction>{props.action}</ListItemSecondaryAction>
  </ListItem>
);

const GoogleTurnedOff = () => (
  <ProviderRow
    title="Google"
    description="Google sign-in is turned off; Backstage signs in with Microsoft"
    action={
      <Button variant="outlined" color="primary" aria-label="Sign in to Google (turned off)" disabled>
        Sign in
      </Button>
    }
  />
);

const GitHubSignOutOnly = () => {
  const api = useApi(githubAuthApiRef);
  const errorApi = useApi(errorApiRef);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const subscription = api
      .sessionState$()
      .subscribe(state => setSignedIn(state === SessionState.SignedIn));
    // Loads an existing session from its cookie without prompting, as Backstage's own
    // row does; until then the state reads signed-out.
    api.getProfile({ optional: true }).catch(() => undefined);
    return () => subscription.unsubscribe();
  }, [api]);

  return (
    <ProviderRow
      title="GitHub"
      description={
        signedIn
          ? 'Signed in from a GitHub tab; sign out here to end or switch the session'
          : 'Sign-in from Settings is turned off; the catalog GitHub tabs sign in when needed'
      }
      action={
        signedIn ? (
          <Button
            variant="outlined"
            color="primary"
            aria-label="Sign out of GitHub"
            onClick={() => api.signOut().catch(error => errorApi.post(error))}
          >
            Sign out
          </Button>
        ) : (
          <Button variant="outlined" color="primary" aria-label="Sign in to GitHub (turned off)" disabled>
            Sign in
          </Button>
        )
      }
    />
  );
};

export const M42ProviderSettings = () => {
  const configApi = useApi(configApiRef);
  const configuredProviders = configApi.getOptionalConfig('auth.providers')?.keys() ?? [];
  const isDevelopment = configApi.getOptionalString('auth.environment') === 'development';

  if (isDevelopment) {
    return <DefaultProviderSettings configuredProviders={configuredProviders} />;
  }
  return (
    <>
      <ProviderSettingsItem
        title={m42EnterpriseProviderConfig.title}
        description="Provides authentication towards Mission 42 Microsoft (Entra) identities"
        icon={Star}
        apiRef={m42EnterpriseAuthApiRef}
      />
      {configuredProviders.includes('google') && <GoogleTurnedOff />}
      {configuredProviders.includes('github') && <GitHubSignOutOnly />}
    </>
  );
};
